<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from '@axios'
import moment from 'moment-timezone'

const props = defineProps({
  restaurantId: {
    type: String,
    required: true,
  },
  memberUserId: {
    type: String,
    required: true,
  },

  // Optional: when the panel is opened from a specific reservation, attach
  // the reservationId to new feedback entries for context.
  reservationId: {
    type: String,
    default: null,
  },

  // Visual density — 'compact' for inline panels on a busy page,
  // 'comfortable' (default) for the dedicated section.
  density: {
    type: String,
    default: 'comfortable',
  },
})

const CATEGORIES = [
  { value: 'no_show', label: 'No-show' },
  { value: 'excessive_lateness', label: 'Excessive lateness' },
  { value: 'party_size', label: 'Party size issue' },
  { value: 'payment_chargeback', label: 'Payment/chargeback issue' },
  { value: 'staff_treatment', label: 'Staff treatment' },
  { value: 'reservation_misuse', label: 'Reservation misuse' },
  { value: 'other', label: 'Other' },
]

const entries = ref([])
const loading = ref(false)
const submitting = ref(false)
const errorMessage = ref(null)

const showAddForm = ref(false)
const newCategory = ref('')
const newNote = ref('')
const newAutoDecline = ref(false)

const autoDeclineActive = computed(() => entries.value.some(e => e.autoDeclineActive))

const baseUrl = '/restaurant-admin/member-feedback'

const fetchEntries = async () => {
  loading.value = true
  errorMessage.value = null
  try {
    const res = await axios.get(baseUrl, {
      params: {
        restaurantId: props.restaurantId,
        memberUserId: props.memberUserId,
      },
    })
    entries.value = res.data || []
  } catch (err) {
    errorMessage.value = err.response?.data?.error || 'Failed to load feedback'
  } finally {
    loading.value = false
  }
}

const submitNew = async () => {
  if (!newCategory.value) {
    errorMessage.value = 'Choose a category'
    
    return
  }
  submitting.value = true
  errorMessage.value = null
  try {
    await axios.post(
      baseUrl,
      {
        restaurantId: props.restaurantId,
        memberUserId: props.memberUserId,
        category: newCategory.value,
        internalNotes: newNote.value,
        autoDeclineActive: newAutoDecline.value,
        reservationId: props.reservationId,
      },
      { params: {} },
    )
    newCategory.value = ''
    newNote.value = ''
    newAutoDecline.value = false
    showAddForm.value = false
    await fetchEntries()
  } catch (err) {
    errorMessage.value = err.response?.data?.error || 'Failed to save'
  } finally {
    submitting.value = false
  }
}

const toggleAutoDecline = async entry => {
  try {
    await axios.patch(
      `${baseUrl}/${entry._id}`,
      { autoDeclineActive: !entry.autoDeclineActive },
      { params: {} },
    )
    entry.autoDeclineActive = !entry.autoDeclineActive
  } catch (err) {
    errorMessage.value = err.response?.data?.error || 'Failed to update'
  }
}

const removeEntry = async entry => {
  if (!confirm('Delete this feedback entry?')) return
  try {
    await axios.delete(`${baseUrl}/${entry._id}`, {
      params: {},
    })
    entries.value = entries.value.filter(e => e._id !== entry._id)
  } catch (err) {
    errorMessage.value = err.response?.data?.error || 'Failed to delete'
  }
}

const categoryLabel = value => CATEGORIES.find(c => c.value === value)?.label || value

watch(
  () => `${props.restaurantId}|${props.memberUserId}`,
  () => fetchEntries(),
)

onMounted(fetchEntries)
</script>

<template>
  <div class="rm-feedback-panel">
    <div class="d-flex align-center justify-space-between mb-2">
      <div class="text-subtitle-2 text-medium-emphasis">
        Member feedback
      </div>
      <VChip
        v-if="autoDeclineActive"
        color="error"
        size="small"
        variant="tonal"
      >
        Auto-decline active
      </VChip>
    </div>

    <VAlert
      v-if="errorMessage"
      type="error"
      density="compact"
      class="mb-2"
    >
      {{ errorMessage }}
    </VAlert>

    <div
      v-if="loading"
      class="text-caption text-medium-emphasis"
    >
      Loading…
    </div>

    <template v-else>
      <div
        v-if="!entries.length"
        class="text-caption text-medium-emphasis mb-2"
      >
        No feedback recorded.
      </div>

      <VList
        v-else
        density="compact"
        class="pa-0 mb-2"
      >
        <VListItem
          v-for="entry in entries"
          :key="entry._id"
          class="px-0"
        >
          <VListItemTitle class="text-body-2">
            <span class="font-weight-medium">{{ categoryLabel(entry.category) }}</span>
            <span class="text-caption text-medium-emphasis ml-2">
              {{ moment(entry.createdAt).format('MMM D, YYYY') }}
            </span>
          </VListItemTitle>
          <VListItemSubtitle
            v-if="entry.internalNotes"
            class="text-caption mt-1"
            style="white-space: normal;"
          >
            {{ entry.internalNotes }}
          </VListItemSubtitle>
          <template #append>
            <div class="d-flex align-center gap-1">
              <VTooltip text="Auto-decline future requests to this restaurant">
                <template #activator="{ props: tip }">
                  <VSwitch
                    v-bind="tip"
                    :model-value="entry.autoDeclineActive"
                    color="error"
                    density="compact"
                    hide-details
                    @update:model-value="toggleAutoDecline(entry)"
                  />
                </template>
              </VTooltip>
              <VBtn
                icon
                size="x-small"
                variant="text"
                @click="removeEntry(entry)"
              >
                <VIcon size="16">
                  mdi-delete-outline
                </VIcon>
              </VBtn>
            </div>
          </template>
        </VListItem>
      </VList>

      <div v-if="!showAddForm">
        <VBtn
          size="small"
          variant="tonal"
          color="primary"
          @click="showAddForm = true"
        >
          Add feedback
        </VBtn>
      </div>

      <div
        v-else
        class="mt-2"
      >
        <VSelect
          v-model="newCategory"
          :items="CATEGORIES"
          item-title="label"
          item-value="value"
          label="Category"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-2"
        />
        <VTextarea
          v-model="newNote"
          label="Internal notes (optional)"
          rows="2"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-2"
        />
        <VCheckbox
          v-model="newAutoDecline"
          label="Auto-decline future requests from this member to this restaurant"
          density="compact"
          hide-details
          class="mb-2"
        />
        <div class="d-flex gap-2">
          <VBtn
            size="small"
            color="primary"
            :loading="submitting"
            :disabled="submitting"
            @click="submitNew"
          >
            Save feedback
          </VBtn>
          <VBtn
            size="small"
            variant="text"
            :disabled="submitting"
            @click="showAddForm = false; newCategory = ''; newNote = ''; newAutoDecline = false"
          >
            Cancel
          </VBtn>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.rm-feedback-panel {
  font-size: 14px;
}
</style>
